import React, { useEffect, useRef, useState } from 'react';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import LeftAndRightArrowButtons from '../LeftAndRightArrowButtons/LeftAndRightArrowButtons';
import {
  CardContainer,
  CarouselContainer,
  CarouselRail,
  FullWidthCarouselContainer,
  LeftAlignedCarouselContainer,
} from './LeftAlignedCarousel.styles';
import useElementResize from 'src/hooks/useElementResize';
import useWindowResize from 'src/hooks/useWindowResize';

interface LeftAlignedCarouselProps {
  children: React.ReactNode[];
}

const GAP_BETWEEN_CARDS = 'spacing-3xl';
const GAP_BETWEEN_CARDS_IN_PIXELS = 24;

// Since a few browsers like Safari does not respect the exact amount of pixels to scroll, we need to add an extra pixel compensation
const EXTRA_PIXEL_COMPENSATION = 4;

const LeftAlignedCarousel = ({ children }: LeftAlignedCarouselProps): JSX.Element => {
  const [hideArrows, setHideArrows] = useState(false);

  const carouselRailRef = useRef<HTMLDivElement>(null);
  const placeholderCardRef = useRef<HTMLDivElement>(null);
  const scrollableElementRef = useRef<HTMLDivElement>(null);
  const fullWidthCarouselContainerRef = useRef<HTMLDivElement>(null);

  const { height: carouselHeight } = useElementResize(carouselRailRef);
  const { width: placeholderCardWidth } = useElementResize(placeholderCardRef);
  const { width: windowWidth } = useWindowResize();

  useEffect(() => {
    if (!scrollableElementRef.current || !carouselRailRef.current || typeof document === 'undefined') return;

    fullWidthCarouselContainerRef.current.style.setProperty('--card-width', `${placeholderCardWidth}px`);

    // Position the fullWidthCarouselContainerRef to the left of the window
    fullWidthCarouselContainerRef.current.style.setProperty(
      '--carousel-left-position',
      `${-placeholderCardRef.current.getBoundingClientRect().left}px`
    );

    // Add padding left on the container based on the left position on the window
    fullWidthCarouselContainerRef.current.style.setProperty(
      '--carousel-padding-left',
      `${placeholderCardRef.current.getBoundingClientRect().left}px`
    );

    // Add margin right based on the left position on the window
    fullWidthCarouselContainerRef.current.style.setProperty(
      '--carousel-margin-right',
      `${placeholderCardRef.current.getBoundingClientRect().left}px`
    );

    // Hide arrows if the scrollableElementRef has nothing to scroll
    const hasScrollableContent = scrollableElementRef.current.scrollWidth > scrollableElementRef.current.clientWidth;

    setHideArrows(!hasScrollableContent);
  }, [placeholderCardWidth, windowWidth]);

  const handleLeftClick = () => {
    if (!scrollableElementRef.current) return;

    const scrollArea = scrollableElementRef.current;
    const spaceScrollPassedLastChild = scrollArea.scrollLeft % (placeholderCardWidth + GAP_BETWEEN_CARDS_IN_PIXELS);

    scrollArea.scrollTo({
      left:
        scrollArea.scrollLeft -
        placeholderCardWidth -
        GAP_BETWEEN_CARDS_IN_PIXELS -
        spaceScrollPassedLastChild +
        EXTRA_PIXEL_COMPENSATION,
      behavior: 'smooth',
    });
  };

  const handleRightClick = () => {
    if (!scrollableElementRef.current) return;

    const scrollArea = scrollableElementRef.current;
    const spaceScrollPassedLastChild = scrollArea.scrollLeft % (placeholderCardWidth + GAP_BETWEEN_CARDS_IN_PIXELS);

    scrollArea.scrollTo({
      left:
        scrollArea.scrollLeft +
        placeholderCardWidth +
        GAP_BETWEEN_CARDS_IN_PIXELS -
        spaceScrollPassedLastChild +
        EXTRA_PIXEL_COMPENSATION,
      behavior: 'smooth',
    });
  };

  const renderPlaceholderElement = () => (
    <div
      ref={placeholderCardRef}
      className='placeholder-card'
      style={{ height: carouselHeight }}
    >
      {children[0]}
    </div>
  );

  return (
    <LeftAlignedCarouselContainer
      direction='column'
      justify='flex-start'
      align='flex-start'
      position='relative'
    >
      <Stack position='relative'>
        {renderPlaceholderElement()}
        <FullWidthCarouselContainer
          position='absolute'
          overflow='hidden'
          paddingBottom='spacing-4xl'
          boxSizing='content-box'
          ref={fullWidthCarouselContainerRef}
        >
          <CarouselContainer
            overflow='auto'
            position='absolute'
            paddingBottom='spacing-4xl'
            ref={scrollableElementRef}
          >
            <CarouselRail
              gap={GAP_BETWEEN_CARDS}
              ref={carouselRailRef}
              paddingRight={GAP_BETWEEN_CARDS}
              marginRight={GAP_BETWEEN_CARDS}
            >
              {children.map((child, index) => (
                <CardContainer key={index}>{child}</CardContainer>
              ))}
            </CarouselRail>
          </CarouselContainer>
        </FullWidthCarouselContainer>
      </Stack>
      {!hideArrows && (
        <Stack marginTop='spacing-4xl'>
          <LeftAndRightArrowButtons
            onLeftClick={handleLeftClick}
            onRightClick={handleRightClick}
            gap='spacing-4xl'
            gapSm='spacing-xl'
          />
        </Stack>
      )}
    </LeftAlignedCarouselContainer>
  );
};

export default LeftAlignedCarousel;
